import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export type ConfirmOptions = {
  title: string;
  description?: React.ReactNode;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'default' | 'destructive' | 'warning';
  onConfirm: () => void;
  // fires only when the cancel button is pressed, never on Esc/X/backdrop
  onCancel?: () => void;
};

const ConfirmContext = React.createContext<(options: ConfirmOptions) => void>(
  () => {},
);

export function useConfirm() {
  return React.useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const [open, setOpen] = React.useState(false);

  const confirm = React.useCallback((next: ConfirmOptions) => {
    setOptions(next);
    setOpen(true);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>{options?.title}</DialogTitle>
            {options?.description && (
              <DialogDescription>{options.description}</DialogDescription>
            )}
          </DialogHeader>
          {options?.body && <DialogBody>{options.body}</DialogBody>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                options?.onCancel?.();
              }}
            >
              {options?.cancelLabel ?? t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant={options?.confirmVariant ?? 'default'}
              onClick={() => {
                setOpen(false);
                options?.onConfirm();
              }}
            >
              {options?.confirmLabel ?? t('common.confirm', 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
